import { AppRole } from '@/types/auth';
import { supabase } from '@/integrations/supabase/client';

/**
 * Client-side permissions cache
 * Loads once and caches for the session
 */
class PermissionsManager {
  private permissions: Map<string, Set<string>> = new Map();
  private loaded = false;

  /**
   * Load permissions from database
   */
  async load(): Promise<void> {
    if (this.loaded) return;

    try {
      const { data, error } = await supabase
        .from('permissions')
        .select('role, resource, action');

      if (error) {
        console.error('[Permissions] Failed to load:', error);
        return;
      }

      // Build lookup map: role:resource -> Set<action>
      data.forEach(({ role, resource, action }) => {
        const key = `${role}:${resource}`;
        if (!this.permissions.has(key)) {
          this.permissions.set(key, new Set());
        }
        this.permissions.get(key)!.add(action);
      });

      this.loaded = true;
      console.log('[Permissions] Loaded permissions for', this.permissions.size, 'role-resource pairs');
    } catch (error) {
      console.error('[Permissions] Unexpected error:', error);
    }
  }

  /**
   * Check if a role can perform an action on a resource
   */
  can(role: AppRole, resource: string, action: string): boolean {
    const key = `${role}:${resource}`;
    return this.permissions.get(key)?.has(action) || false;
  }

  /**
   * Check if user with multiple roles can perform an action
   */
  userCan(roles: AppRole[], resource: string, action: string, isSuperAdmin: boolean = false): boolean {
    // Super admins can do anything
    if (isSuperAdmin) return true;

    // Check if any role has the permission
    return roles.some(role => this.can(role, resource, action));
  }

  /**
   * Get all actions a role can perform on a resource
   */
  getActions(role: AppRole, resource: string): string[] {
    const key = `${role}:${resource}`;
    return Array.from(this.permissions.get(key) || []);
  }

  /**
   * Clear cache (useful for testing or refresh)
   */
  clear(): void {
    this.permissions.clear();
    this.loaded = false;
  }
}

export const permissionsManager = new PermissionsManager();

/**
 * Hook-style helper for components
 * Usage: if (can('recipes', 'create')) { ... }
 */
export const createPermissionChecker = (
  roles: AppRole[],
  isSuperAdmin: boolean = false
) => {
  return (resource: string, action: string): boolean => {
    return permissionsManager.userCan(roles, resource, action, isSuperAdmin);
  };
};
